/*
 * NASQA — offer engine.
 *
 * Drives every countdown on the storefront from one clock and one schedule.
 *
 * Design rules this file holds to:
 *
 *   1. It never invents an offer. Windows are authored as blocks on the
 *      flash-bar section and must mirror a real scheduled discount in the
 *      Shopify admin. When the schedule runs out the bar falls back to the
 *      evergreen message instead of restarting a fake timer.
 *
 *   2. The clock is corrected against the server timestamp Liquid stamps on
 *      the bar, so a visitor with a wrong device clock still sees the true
 *      remaining time.
 *
 *   3. One interval for the whole page. Every countdown subscribes to it.
 *
 *   4. Digits roll on the compositor (transform only). Under
 *      prefers-reduced-motion they swap without animation.
 */
(() => {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------- clock */

  let skew = 0;
  const seed = document.querySelector('[data-flash-now]');
  if (seed) {
    const serverNow = Number(seed.getAttribute('data-flash-now'));
    if (Number.isFinite(serverNow) && serverNow > 0) skew = serverNow * 1000 - Date.now();
  }
  const now = () => Date.now() + skew;

  /* ------------------------------------------------------------ scheduler */

  const subscribers = new Set();
  let timer = null;

  const fire = () => {
    const t = now();
    subscribers.forEach((fn) => {
      try { fn(t); } catch (e) { /* one bad subscriber must not stop the clock */ }
    });
  };

  // Align to the second boundary so every digit on the page turns together.
  const schedule = () => {
    clearTimeout(timer);
    const drift = now() % 1000;
    timer = setTimeout(() => { fire(); schedule(); }, 1000 - drift);
  };

  const start = () => { if (subscribers.size) { fire(); schedule(); } };
  const stop = () => clearTimeout(timer);

  const subscribe = (fn) => {
    subscribers.add(fn);
    if (subscribers.size === 1 && !document.hidden) start();
    fn(now());
  };

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
    else start();
  });

  /* ------------------------------------------------------------- odometer */

  const ARABIC = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  const useArabic = (document.documentElement.lang || '').slice(0, 2) === 'ar';
  const glyphs = useArabic ? ARABIC : ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  /* A vertical 0–9 strip translated by -value*(100/11)%. The strip carries a
     repeated 0 at the end so 9→0 rolls forward and then snaps back silently,
     instead of spinning backwards through eight digits. */
  class Digit {
    constructor(host) {
      this.value = -1;
      this.el = document.createElement('span');
      this.el.className = 'fclock__digit';
      this.strip = document.createElement('span');
      this.strip.className = 'fclock__strip';
      this.strip.setAttribute('aria-hidden', 'true');
      glyphs.concat(glyphs[0]).forEach((g) => {
        const b = document.createElement('b');
        b.textContent = g;
        this.strip.appendChild(b);
      });
      this.el.appendChild(this.strip);
      host.appendChild(this.el);
    }

    set(v) {
      if (v === this.value) return;
      const rolling = this.value === 9 && v === 0;
      const wrapped = this.value === 0 && v === 9; // going up after a snap
      const target = rolling ? 10 : v;

      if (reduced.matches || this.value === -1 || wrapped) {
        this.strip.style.transition = 'none';
        this.strip.style.transform = `translateY(${-v * (100 / 11)}%)`;
        // force a reflow so the next transition is not swallowed
        void this.strip.offsetHeight;
        this.strip.style.transition = '';
      } else {
        this.strip.style.transform = `translateY(${-target * (100 / 11)}%)`;
        if (rolling) {
          const snap = () => {
            this.strip.removeEventListener('transitionend', snap);
            this.strip.style.transition = 'none';
            this.strip.style.transform = 'translateY(0%)';
            void this.strip.offsetHeight;
            this.strip.style.transition = '';
          };
          this.strip.addEventListener('transitionend', snap);
        }
      }
      this.value = v;
    }
  }

  /* ---------------------------------------------------------- countdown UI */

  const UNIT_ORDER = ['d', 'h', 'm', 's'];

  class Clock {
    /* host: element carrying data-units="d,h,m,s" and optional data-unit-* labels */
    constructor(host) {
      this.host = host;
      this.host.setAttribute('dir', 'ltr');
      this.units = (host.dataset.units || 'h,m,s')
        .split(',')
        .map((u) => u.trim())
        .filter((u) => UNIT_ORDER.includes(u));
      this.groups = {};
      this.host.textContent = '';

      this.units.forEach((u, i) => {
        let sep = null;
        if (i > 0) {
          sep = document.createElement('i');
          sep.className = 'fclock__sep';
          sep.textContent = ':';
          sep.setAttribute('aria-hidden', 'true');
          this.host.appendChild(sep);
        }
        const cell = document.createElement('span');
        cell.className = 'fclock__cell';
        const digits = document.createElement('span');
        digits.className = 'fclock__digits';
        cell.appendChild(digits);

        /* Captions are opt-in per clock (data-cap-*), separately from the unit
           words used for the spoken sentence (data-unit-*) — the bar wants the
           words without the captions. */
        const label = host.dataset['cap' + u.toUpperCase()];
        if (label) {
          const cap = document.createElement('small');
          cap.className = 'fclock__cap';
          cap.textContent = label;
          cell.appendChild(cap);
        }
        this.host.appendChild(cell);
        this.groups[u] = { host: digits, digits: [], cell: cell, sep: sep };
      });

      /* Screen readers get a coarse, human sentence — never a per-second
         torrent. The rolling strips are aria-hidden, so this span is the only
         thing assistive tech sees here. */
      this.sr = document.createElement('span');
      this.sr.className = 'visually-hidden';
      /* The live region is this span, not the card around it. With role=status
         on the container, every message swap re-announced the whole offer. */
      this.sr.setAttribute('aria-live', 'polite');
      this.host.appendChild(this.sr);

      this.words = {
        d: host.dataset.unitD || '',
        h: host.dataset.unitH || '',
        m: host.dataset.unitM || '',
      };
    }

    /* seconds: whole seconds remaining */
    render(seconds) {
      const s = Math.max(0, Math.floor(seconds));
      const parts = {
        d: Math.floor(s / 86400),
        h: this.units.includes('d') ? Math.floor((s % 86400) / 3600) : Math.floor(s / 3600),
        m: Math.floor((s % 3600) / 60),
        s: s % 60,
      };

      this.units.forEach((u, i) => {
        const group = this.groups[u];

        /* A leading "00 days" reads as filler and softens the deadline. Drop
           the cell (and the separator that follows it) until it has a value. */
        if (u === 'd' && parts.d === 0) {
          group.cell.hidden = true;
          const next = this.groups[this.units[i + 1]];
          if (next && next.sep) next.sep.hidden = true;
          return;
        }
        if (u === 'd') {
          group.cell.hidden = false;
          const next = this.groups[this.units[i + 1]];
          if (next && next.sep) next.sep.hidden = false;
        }

        const text = String(parts[u]).padStart(2, '0');
        while (group.digits.length < text.length) group.digits.push(new Digit(group.host));
        for (let j = 0; j < text.length; j += 1) group.digits[j].set(Number(text[j]));
      });
      return parts;
    }

    /* Spoken once a minute: "ينتهي خلال ٣ يوم ١٦ ساعة ٥٧ دقيقة".
       Units with a leading zero are dropped so the sentence stays short. */
    announce(parts, prefix) {
      const said = [];
      if (parts.d) said.push(`${parts.d} ${this.words.d}`);
      if (parts.d || parts.h) said.push(`${parts.h} ${this.words.h}`);
      said.push(`${parts.m} ${this.words.m}`);
      const text = `${prefix || ''} ${said.join(' ')}`.trim();
      if (this.sr.textContent !== text) this.sr.textContent = text;
    }
  }

  /* ------------------------------------------------------- window resolver */

  const parseWindows = (raw) => {
    let list = [];
    try { list = JSON.parse(raw) || []; } catch (e) { return []; }
    return list
      .map((w) => ({
        id: String(w.id || ''),
        start: Number(w.start) * 1000,
        end: Number(w.end) * 1000,
        message: w.message || '',
        cta: w.cta || '',
        url: w.url || '',
        handle: w.handle || '',
        scope: w.scope || 'all',
      }))
      .filter((w) => Number.isFinite(w.start) && Number.isFinite(w.end) && w.end > w.start)
      .sort((a, b) => a.start - b.start);
  };

  /* Returns { state: 'live'|'soon'|'idle', window, until } */
  const resolve = (windows, t) => {
    const live = windows.find((w) => t >= w.start && t < w.end);
    if (live) return { state: 'live', window: live, until: live.end };
    const next = windows.find((w) => w.start > t);
    if (next) return { state: 'soon', window: next, until: next.start };
    return { state: 'idle', window: null, until: 0 };
  };

  /* --------------------------------------------------------------- the bar */

  const initBar = () => {
    const bar = document.querySelector('[data-flash-bar]');
    if (!bar) return;

    const source = bar.querySelector('[data-flash-windows]');
    const windows = source ? parseWindows(source.textContent) : [];

    const msgEl = bar.querySelector('[data-flash-msg]');
    const labelEl = bar.querySelector('[data-flash-label]');
    const clockEl = bar.querySelector('[data-flash-clock]');
    const ctaEl = bar.querySelector('[data-flash-cta]');
    const timerWrap = bar.querySelector('[data-flash-timer]');

    const labels = {
      live: bar.dataset.labelLive || '',
      soon: bar.dataset.labelSoon || '',
    };
    const idleMessage = bar.dataset.idleMessage || '';
    const idleUrl = bar.dataset.idleUrl || '';
    const idleCta = bar.dataset.idleCta || '';

    const clock = clockEl ? new Clock(clockEl) : null;
    let shownId = null;

    /* Dismissal is per offer window, not per session: a visitor who closed
       last week's bar should still see this week's. */
    const dismissKey = (id) => `nasqa-flash-dismissed:${id}`;
    const isDismissed = (id) => {
      try { return sessionStorage.getItem(dismissKey(id)) === '1'; } catch (e) { return false; }
    };

    bar.querySelector('[data-flash-close]')?.addEventListener('click', () => {
      bar.hidden = true;
      document.documentElement.style.removeProperty('--flash-bar-height');
      try { if (shownId) sessionStorage.setItem(dismissKey(shownId), '1'); } catch (e) {}
    });

    const paint = (t) => {
      const { state, window: w, until } = resolve(windows, t);
      const id = state === 'idle' ? 'idle' : `${state}:${w.id}`;
      const switched = id !== shownId;

      if (switched) {
        shownId = id;

        if (isDismissed(id)) { bar.hidden = true; return; }

        const message = state === 'idle' ? idleMessage : w.message;
        if (!message) { bar.hidden = true; return; }

        if (msgEl) msgEl.textContent = message;
        if (labelEl) labelEl.textContent = state === 'idle' ? '' : labels[state];

        const url = state === 'idle' ? idleUrl : w.url;
        const ctaText = state === 'idle' ? idleCta : w.cta;
        if (ctaEl) {
          if (url && ctaText) {
            ctaEl.href = url;
            ctaEl.textContent = ctaText;
            ctaEl.hidden = false;
          } else {
            ctaEl.hidden = true;
          }
        }
        if (timerWrap) timerWrap.hidden = state === 'idle';
        bar.dataset.state = state;
        bar.hidden = false;
        bar.classList.add('is-in');
      }

      if (clock && state !== 'idle') {
        const left = Math.max(0, Math.round((until - t) / 1000));
        const parts = clock.render(left);
        if (switched || parts.s === 0) clock.announce(parts, labels[state]);
      }
    };

    subscribe(paint);
  };

  /* -------------------------------------------------- product-page offer */

  /*
   * The product page reads the same schedule the bar does — there is one
   * source of offer truth per page, so the two can never disagree.
   */
  const initInline = () => {
    const nodes = document.querySelectorAll('[data-flash-inline]');
    if (!nodes.length) return;

    const source = document.querySelector('[data-flash-windows]');
    if (!source) return;
    const windows = parseWindows(source.textContent);
    if (!windows.length) return;

    nodes.forEach((node) => {
      const handle = node.dataset.flashProduct || '';
      const clockEl = node.querySelector('[data-flash-clock]');
      if (!clockEl) return;

      const clock = new Clock(clockEl);
      const titleEl = node.querySelector('[data-offer-title]');
      const noteEl = node.querySelector('[data-offer-note]');
      const linkEl = node.querySelector('[data-offer-link]');
      const labelEl = node.querySelector('[data-offer-label]');

      const copy = {
        auto: node.dataset.noteAuto || '',
        elsewhere: node.dataset.copyElsewhere || '',
        ctaElsewhere: node.dataset.ctaElsewhere || '',
        live: node.dataset.labelLive || '',
        soon: node.dataset.labelSoon || '',
      };

      let painted = null;
      let switched = false;

      subscribe((t) => {
        const { state, window: w, until } = resolve(windows, t);

        /* A window with no handle is storewide and therefore applies here. */
        const applies = state !== 'idle' && (!w.handle || w.handle === handle);

        let mode = 'none';
        if (state === 'live') mode = applies ? 'mine' : 'elsewhere';
        else if (state === 'soon' && applies) mode = 'soon';

        if (mode === 'none') { node.hidden = true; painted = null; return; }

        const key = `${mode}:${w.id}`;
        switched = key !== painted;
        if (switched) {
          painted = key;
          node.hidden = false;
          node.classList.toggle('offer--elsewhere', mode !== 'mine');

          if (mode === 'elsewhere') {
            if (titleEl) titleEl.textContent = copy.elsewhere;
            if (noteEl) noteEl.textContent = w.message;
            if (linkEl) {
              if (w.url && copy.ctaElsewhere) {
                linkEl.href = w.url;
                linkEl.textContent = copy.ctaElsewhere;
                linkEl.hidden = false;
              } else {
                linkEl.hidden = true;
              }
            }
          } else {
            if (titleEl) titleEl.textContent = w.message;
            if (noteEl) noteEl.textContent = copy.auto;
            if (linkEl) linkEl.hidden = true;
          }
          if (labelEl) labelEl.textContent = mode === 'soon' ? copy.soon : copy.live;
        }

        const parts = clock.render(Math.round((until - t) / 1000));
        if (switched || parts.s === 0) {
          clock.announce(parts, mode === 'soon' ? copy.soon : copy.live);
        }
      });
    });
  };

  /* ------------------------------------------------ same-day dispatch cut-off */

  /*
   * Counts to the next dispatch cut-off in the market's own timezone, and
   * skips the days the store does not dispatch on. Both come from theme
   * settings, so if the operation changes the countdown follows it.
   */
  const initDispatch = () => {
    const node = document.querySelector('[data-dispatch]');
    if (!node) return;

    const clockEl = node.querySelector('[data-flash-clock]');
    if (!clockEl) return;

    const hour = Number(node.dataset.cutoffHour || 16);
    const offset = Number(node.dataset.utcOffset || 3);       // market offset in hours
    const offDays = (node.dataset.offDays || '5,6')            // 0=Sun … 6=Sat
      .split(',')
      .map((d) => Number(d.trim()))
      .filter((d) => d >= 0 && d <= 6);

    const clock = new Clock(clockEl);
    const todayLine = node.dataset.copyToday || '';
    const nextLine = node.dataset.copyNext || '';
    const copyEl = node.querySelector('[data-dispatch-copy]');
    let seeded = false;

    /* Work in market-local time by shifting the epoch, then reading UTC parts. */
    const marketDate = (t) => new Date(t + offset * 3600 * 1000);

    subscribe((t) => {
      const local = marketDate(t);
      let cutoff = Date.UTC(
        local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate(), hour, 0, 0
      ) - offset * 3600 * 1000;

      let sameDay = true;
      let guard = 0;
      // Roll forward past the cut-off and past any non-dispatch day.
      while (guard < 10) {
        const day = marketDate(cutoff).getUTCDay();
        if (cutoff > t && !offDays.includes(day)) break;
        cutoff += 86400000;
        sameDay = false;
        guard += 1;
      }

      const parts = clock.render(Math.round((cutoff - t) / 1000));
      /* The prefix and suffix around this clock are already in the DOM, so the
         span only has to supply the duration itself. */
      if (!seeded || parts.s === 0) { clock.announce(parts, ''); seeded = true; }

      if (copyEl) {
        const line = sameDay ? todayLine : nextLine;
        if (line && copyEl.textContent !== line) copyEl.textContent = line;
      }
    });
  };

  /* ------------------------------------------------------------------ boot */

  const boot = () => {
    initBar();
    initInline();
    initDispatch();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
